import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// =============================================
// Bilmottak (Truck Receiving) Types - Modus 4
// =============================================

/**
 * Predefined types for Bilmottak sessions
 */
export enum BilmottakType {
  /** Custom free-text name */
  Custom = 0,
  /** Morning truck delivery */
  Morgenleveranse = 1,
  /** Afternoon truck delivery */
  Ettermiddagsleveranse = 2,
  /** Express delivery */
  Ekspresslevering = 3,
  /** Return goods */
  Returvarer = 4,
  /** Cross-dock shipment */
  CrossDock = 5
}

/**
 * Get display name for BilmottakType
 */
export function getBilmottakTypeName(type: BilmottakType): string {
  switch (type) {
    case BilmottakType.Morgenleveranse:
      return 'Morgenleveranse';
    case BilmottakType.Ettermiddagsleveranse:
      return 'Ettermiddagsleveranse';
    case BilmottakType.Ekspresslevering:
      return 'Ekspresslevering';
    case BilmottakType.Returvarer:
      return 'Returvarer';
    case BilmottakType.CrossDock:
      return 'Cross-Dock';
    default:
      return 'Egendefinert';
  }
}

/**
 * Request DTO for creating a new Bilmottak session
 */
export interface CreateBilmottakRequest {
  /** Session name - either free text or predefined type name */
  name: string;
  /** Predefined type (optional, use Custom=0 for free-text names) */
  type?: BilmottakType;
  /** Warehouse ID where the receiving takes place */
  warehouse: string;
  /** Optional description/notes for the session */
  description?: string;
  /** Optional list of purchase order numbers to link to this session */
  selectedOrderNos?: number[];
}

/**
 * DTO for an active Bilmottak session with operator count and progress
 */
export interface ActiveBilmottakSessionDto {
  /** The BatchID of the session (format: TRUCK_{StatID}) */
  batchId: string;
  /** StatID of the StcTransOrder */
  statId: number;
  /** Session name/description */
  name: string;
  /** Warehouse where receiving takes place */
  warehouse: string;
  /** When the session was created */
  createdAt: string;
  /** Who created the session */
  createdBy: string;
  /** Current number of active operators working on this session */
  activeOperatorCount: number;
  /** List of operator IDs currently working on this session */
  activeOperators: string[];
  /** Total number of items/lines received so far */
  totalLinesReceived: number;
  /** Total quantity received so far */
  totalQuantityReceived: number;
  /** Number of distinct purchase orders touched */
  ordersAffected: number;
  /** Current order status (20 = In Progress, 50 = Ready for export) */
  orderStatus: number;
  /** Status text for display */
  statusText: string;
  /** Whether the current user is already participating in this session */
  isCurrentUserParticipating: boolean;
}

/**
 * Response DTO when creating a new Bilmottak session
 */
export interface CreateBilmottakResponse {
  /** Whether the creation was successful */
  success: boolean;
  /** Error message if creation failed */
  errorMessage?: string;
  /** The created session details */
  session?: ActiveBilmottakSessionDto;
  /** The BatchID to use for receiving */
  batchId: string;
}

/**
 * Response DTO when joining an existing Bilmottak session
 */
export interface JoinBilmottakResponse {
  /** Whether joining was successful */
  success: boolean;
  /** Error message if joining failed */
  errorMessage?: string;
  /** The BatchID to use for receiving */
  batchId: string;
  /** Session details after joining */
  session?: ActiveBilmottakSessionDto;
  /** Whether this operator was already participating */
  wasAlreadyParticipating: boolean;
}

/**
 * Service for Bilmottak (Truck Receiving) operations - Modus 4
 */
class BilmottakService {
  private getAuthHeaders(): HeadersInit {
    const token = AuthService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Create a new Bilmottak session for multi-operator truck receiving.
   * Creates a StcTransOrder with OrderType = 4 and generates a TRUCK_{StatID} BatchID.
   */
  async createSession(request: CreateBilmottakRequest): Promise<CreateBilmottakResponse> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        errorMessage: errorData.errorMessage || `HTTP ${response.status}: ${response.statusText}`,
        batchId: ''
      };
    }

    return response.json();
  }

  /**
   * Get all active Bilmottak sessions.
   * Returns sessions that are in progress (OrderStatus = 20) with operator counts and progress.
   */
  async getActiveSessions(): Promise<ActiveBilmottakSessionDto[]> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak/active`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      console.error('Failed to get active Bilmottak sessions:', response.status, response.statusText);
      return [];
    }

    return response.json();
  }

  /**
   * Join an existing Bilmottak session.
   * Validates the session exists and is active, then returns session details.
   */
  async joinSession(batchId: string): Promise<JoinBilmottakResponse> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak/${encodeURIComponent(batchId)}/join`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        errorMessage: errorData.errorMessage || `HTTP ${response.status}: ${response.statusText}`,
        batchId: batchId,
        wasAlreadyParticipating: false
      };
    }

    return response.json();
  }

  /**
   * Link purchase orders to an existing Bilmottak session.
   * Sets InboundShipmentNo = batchId on POrderLines for the specified orders.
   */
  async linkOrders(batchId: string, orderNos: number[]): Promise<{ success: boolean; errorMessage?: string; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak/${encodeURIComponent(batchId)}/link-orders`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ orderNos })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        errorMessage: errorData.errorMessage || `HTTP ${response.status}: ${response.statusText}`
      };
    }

    return response.json();
  }

  /**
   * Unlink purchase orders from a Bilmottak session.
   * Clears InboundShipmentNo on POrderLines where it matches the batchId.
   */
  async unlinkOrders(batchId: string, orderNos: number[]): Promise<{ success: boolean; errorMessage?: string; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak/${encodeURIComponent(batchId)}/unlink-orders`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ orderNos })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        errorMessage: errorData.errorMessage || `HTTP ${response.status}: ${response.statusText}`
      };
    }

    return response.json();
  }

  /**
   * Get distinct orders in a Bilmottak session with line counts and quantities.
   * Used to populate per-order freight cost inputs in the close dialog.
   */
  async getSessionOrders(batchId: string): Promise<BilmottakOrderSummary[]> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak/${encodeURIComponent(batchId)}/orders`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      console.error('Failed to get session orders:', response.status, response.statusText);
      return [];
    }

    return response.json();
  }

  /**
   * Close a Bilmottak session: triggers ERP export with per-order freight cost
   * and sets OrderStatus to 60 (Eksportert).
   */
  async closeBilmottak(batchId: string, freightCostPerOrder?: Record<string, number>, description?: string): Promise<CloseBilmottakResponse> {
    const response = await fetch(`${API_BASE_URL}/stocktrans/bilmottak/${encodeURIComponent(batchId)}/close`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        freightCostPerOrder: freightCostPerOrder ?? null,
        freightCostDescription: description ?? null
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        errorMessage: errorData.errorMessage || `HTTP ${response.status}: ${response.statusText}`,
        exportedFiles: 0,
        exportedRecords: 0,
        processedAt: new Date().toISOString()
      };
    }

    return response.json();
  }
}

/**
 * Summary of a single order within a Bilmottak session
 */
export interface BilmottakOrderSummary {
  /** ERP order number (e.g. "RND-PO-26") */
  orderNoERP: string;
  /** Internal POrder.OrderNo */
  orderNo: number;
  /** Number of received lines for this order */
  lineCount: number;
  /** Total quantity received for this order */
  totalQuantity: number;
}

/**
 * Response DTO after closing a Bilmottak session
 */
export interface CloseBilmottakResponse {
  success: boolean;
  errorMessage?: string;
  exportedFiles: number;
  exportedRecords: number;
  processedAt: string;
}

export const bilmottakService = new BilmottakService();
